import {
  DndProvider,
  DropOptions,
  getBackendOptions,
  getDescendants,
  MultiBackend,
  Tree,
  NodeModel,
  TreeMethods
} from "@minoru/react-dnd-treeview";
import React from "react";
import Node from "./Node";
import Placeholder from "./Placeholder";
// import useTreeOpenHandler from "./useTreeOpenHandler";
// import sampleData from "./sample-default.json";
import styles from "./styles.module.css";


const NewSampleData = [
  {
    id: "1",
    parent: "0",
    text: "Mumbai | 600003 - Bhavesh Barchha - 19377"
  },
  {
    id: "2",
    parent: "0",
    text: "Mumbai | 500014 - R Raghavendran - 840258"
  },
  {
    id: "3",
    parent: "0",
    text: "Gulbarga ZM | 400205 - Chandrahas Agnihotri - 850902",
    // droppable: true
  },
  {
    id: "6",
    parent: "3",
    text: "Ernakulam Rm | 300316 - SHINE S N - 882029",
    // droppable: true
  },
  {
    id: "9",
    parent: "6",
    text: "Ernakulam | 200307 - Abin Baby - 871181",
    // droppable: true
  },
  {
    id: "10",
    parent: "9",
    text: "Quilon | 101084 - Vyshnav J. Pillai - 881690",
    // droppable: true
  },
  {
    id: "12",
    parent: "10",
    text: "Kottayam | 101085 - Alan Biju - 881029"
  },
  {
    id: "13",
    parent: "10",
    text: "Trivandrum | 103544 - Midhun B. - 877680"
  },
  {
    id: "14",
    parent: "10",
    text: "Ernakulam | 101063 - Afrideen A - 880266"
  },
  {
    id: "11",
    parent: "9",
    text: "Calicut | 200313 - Muhammed Rafi NK - 878635"
  },
  {
    id: "7",
    parent: "3",
    text: "Gulbarga Rm | 300513 - Mahadev Wadevar - 870021"
  },
  {
    id: "8",
    parent: "3",
    text: "Bangalore Rm | 300114 - B K Naveen - 825887"
  },
  {
    id: "4",
    parent: "0",
    text: "Hyderabad ZM | 400094 - Nalla Ganesh Reddy - 818100"
  },
  {
    id: "5",
    parent: "0",
    text: "West ZM | 400028 - Dattatreya Uttam Khade - 818650"
  }
];




const useTreeOpenHandler = () => {
  const ref = React.useRef<TreeMethods | null>(null);

  const [openIds, setOpenIds] = React.useState<(string | number)[]>([]);

  const open = (id: number | string) => {
    ref.current?.open(id);
    setOpenIds((p) => {
      return p.includes(id) ? p : [...p, id];
    });
  };
  const close = (id: number | string) => {
    ref.current?.close(id);
    setOpenIds((p) => {
      return [...p.filter((v) => v !== id)];
    });
  };
  const toggle = (id: number | string) => {
    openIds.includes(id) ? close(id) : open(id);
  };

  const isVisible = (id: number | string, treeData: NodeModel[]): boolean => {
    const parentId = treeData.find((node) => node.id === id)?.parent;
    const parentExistsInTree =
      parentId && treeData.find((node) => node.id === parentId);
    if (parentExistsInTree) {
      const isParentVisible = openIds.includes(parentId);
      return isParentVisible ? isVisible(parentId, treeData) : false;
    } else {
      return true;
    }
  };

  const getPipeHeight = (id: number | string, treeData: NodeModel[]) => {
    treeData = getDescendants(treeData, id);
    const ROW_HEIGHT = 32;
    const LIST_PADDING = 5;

    const droppableHeightExceedsRow = (node: NodeModel) =>
      node?.droppable &&
      openIds.includes(node.id) &&
      treeData.filter((n) => n.parent === node.id).length > 0;

      const getHeightOfId = (id: number | string): number => {
        const directChildren = treeData.filter((node) => node.parent === id);
        const heightOfChildren = directChildren.map((node) =>
          droppableHeightExceedsRow(node)
        ? getHeightOfId(node.id) + ROW_HEIGHT + LIST_PADDING
        : ROW_HEIGHT
      );
      const height = heightOfChildren.reduce((a, b) => a + b, 0);
      return height;
    };
    
    const lastChild = treeData
    .filter((node) => node.parent === id)
    .reverse()[0];
    if (droppableHeightExceedsRow(lastChild)) {
      return getHeightOfId(id) - getHeightOfId(lastChild.id) - LIST_PADDING;
    }
    
    return getHeightOfId(id);
  };
  
  return { ref, open, close, toggle, getPipeHeight, isVisible, openIds };
};

const reorderArray = (
  array: NodeModel[],
  sourceIndex: number,
  targetIndex: number
) => {
  const newArray = [...array];
  const element = newArray.splice(sourceIndex, 1)[0];
  newArray.splice(targetIndex, 0, element);
  return newArray;
};


export default function TreeView({ data,open,showButton=true ,showNode=true}: { data?: NodeModel[],open?:boolean,showButton?:boolean,showNode?:boolean }){
  const { ref, getPipeHeight, toggle } = useTreeOpenHandler();
  const initialData = (data || NewSampleData).map(node => {
    const hasChildren = (data || NewSampleData).some(n => n.parent === node.id);
    return { ...node, droppable: hasChildren };
  });
  
  const [treeData, setTreeData] = React.useState<NodeModel[]>(initialData);

  React.useEffect(() => {
    if (data) {
      const updated = data.map(node => {
        const hasChildren = data.some(n => n.parent === node.id);
        return { ...node, droppable: hasChildren };
      });
      setTreeData(updated);
    }
  }, [data]);

    const allOpenIds = React.useMemo(
    () => (open ? treeData.filter(node => node.droppable).map(node => node.id) : []),
    [open, treeData]
  );

  const handleDrop = (newTree: NodeModel[], e: DropOptions) => {
    const { dragSourceId, dropTargetId, destinationIndex } = e;
    if (
      typeof dragSourceId === "undefined" ||
      typeof dropTargetId === "undefined"
    )
      return;
    const start = treeData.find((v) => v.id === dragSourceId);
    const end = treeData.find((v) => v.id === dropTargetId);

    if (
      start?.parent === dropTargetId &&
      start &&
      typeof destinationIndex === "number"
    ) {
      setTreeData((treeData) => {
        const output = reorderArray(
          treeData,
          treeData.indexOf(start),
          destinationIndex
        );
        return output;
      });
    }

    if (
      start?.parent !== dropTargetId &&
      start &&
      typeof destinationIndex === "number"
    ) {
      if (
        getDescendants(treeData, dragSourceId).find(
          (el) => el.id === dropTargetId
        ) ||
        dropTargetId === dragSourceId ||
        (end && !end?.droppable)
      )
        return;
      setTreeData((treeData) => {
        const output = reorderArray(
          treeData,
          treeData.indexOf(start),
          destinationIndex
        );
        const movedElement = output.find((el) => el.id === dragSourceId);
        if (movedElement) movedElement.parent = dropTargetId;
        return output;
      });
    }
  };

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <div className={styles.wrapper}>
        <Tree
        initialOpen={open ? allOpenIds : false}
          ref={ref}
          classes={{
            root: styles.treeRoot,
            placeholder: styles.placeholder,
            dropTarget: styles.dropTarget,
            listItem: styles.listItem
          }}
          tree={treeData}
          sort={false}
          rootId={'#'}
          insertDroppableFirst={false}
          enableAnimateExpand={true}
          onDrop={handleDrop}
          canDrop={() => false}
          canDrag={()=>false}
          dropTargetOffset={5}
          placeholderRender={(node, { depth }) => (
            <Placeholder node={node} depth={depth} />
          )}
          render={(node, { depth, isOpen, isDropTarget }) => (
            <Node
              getPipeHeight={getPipeHeight}
              isData={showNode}
              showButton={showButton}
              node={node}
              depth={depth}
              isOpen={isOpen}
              onClick={() => {
                if (node.droppable) {
                  toggle(node?.id);
                }
              }}
              isDropTarget={isDropTarget}
              treeData={treeData}
            />
          )}
        />
      </div>
    </DndProvider>
  );
}